<script setup lang="ts">
import type { TableOptions } from '@core/stores/user'
import type { SortItem } from '@core/types'

const { t, te } = useI18n()
const table = 'ai-prompts'

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const activeTab = ref<'slot' | 'custom'>('slot')

const slotHeaders = [
  { title: t(`${table}-page.key`), key: 'key' },
  { title: t(`${table}-page.name`), key: 'name' },
  { title: t(`${table}-page.description`), key: 'description', sortable: false },
  { title: t(`${table}-page.model`), key: 'model' },
  { title: t(`${table}-page.updated_at`), key: 'updated_at' },
  { title: t(`${table}-page.actions`), key: 'actions', sortable: false },
]

const customHeaders = [
  { title: t(`${table}-page.name`), key: 'name' },
  { title: t(`${table}-page.description`), key: 'description', sortable: false },
  { title: t(`${table}-page.model`), key: 'model' },
  { title: t(`${table}-page.used_by`), key: 'used_by', sortable: false },
  { title: t(`${table}-page.updated_at`), key: 'updated_at' },
  { title: t(`${table}-page.actions`), key: 'actions', sortable: false },
]

const headers = computed(() => activeTab.value === 'slot' ? slotHeaders : customHeaders)

const defaultTableOptions: TableOptions = {
  sortBy: 'id',
  orderBy: 'asc',
  page: 1,
  perPage: 10,
}

const cookieTableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

let currentTableOptions: TableOptions | undefined = cookieTableOptions.value

if (currentTableOptions === undefined) {
  currentTableOptions = defaultTableOptions

  const newTableOptions: any = cookieTableOptions.value

  newTableOptions.push(currentTableOptions)

  useCookie(`${table}TableOptions`).value = newTableOptions
}

const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const useLoading = false
const itemsPerPage = ref(currentTableOptions.perPage)
const page = ref(currentTableOptions.page)
const sortBy = ref<string>(currentTableOptions.sortBy)
const orderBy = ref<undefined | boolean | 'asc' | 'desc'>(currentTableOptions.orderBy)
const itemForDelete = ref()
const currenItem = ref()
const isLoading = ref(false)
const isAddDialogOpen = ref(false)
const isDeleteConfirmDialogVisible = ref(false)

const updateOptions = (options: any) => {
  page.value = options.page
  if (options.sortBy[0] !== undefined) {
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
  }
  else {
    sortBy.value = defaultTableOptions.sortBy
    orderBy.value = defaultTableOptions.orderBy
  }
}

const sortTable: SortItem[] = [{ key: sortBy.value, order: orderBy.value }]

const { data: itemsData, execute: fetchItems } = await useApi<any>(createUrl(`/${table}/`,
  {
    query: {
      search: debouncedSearch,
      page,
      itemsPerPage,
      sortBy,
      orderBy,
      type: activeTab,
    },
  },
))

const updateList = async () => {
  isLoading.value = useLoading
  fetchItems().then(() => {
    isLoading.value = false
  })
}

const deleteItem = async () => {
  await $api(`/${table}/`, {
    method: 'DELETE',
    body: {
      id: itemForDelete.value.id,
    },
  })
  await updateList()
}

const deleteConfirmation = async (item: any) => {
  isDeleteConfirmDialogVisible.value = !isDeleteConfirmDialogVisible.value
  itemForDelete.value = item
}

const showEditDialog = async (item: any) => {
  isAddDialogOpen.value = !isAddDialogOpen.value
  currenItem.value = item
}

const items = computed(() => itemsData.value.data)

const total = computed(() => itemsData.value.total)

const slotDescription = (key: string) => {
  const i18nKey = `${table}-page.slot_description.${key}`

  return te(i18nKey) ? t(i18nKey) : '—'
}

const usageCount = (item: any) => (item.competencies_count ?? 0) + (item.ai_profiles_count ?? 0)

const usageText = (item: any) => t(`${table}-page.used_by_value`, {
  competencies: item.competencies_count ?? 0,
  profiles: item.ai_profiles_count ?? 0,
})

watch(activeTab, () => {
  page.value = 1
})

watch([
  isDeleteConfirmDialogVisible, isAddDialogOpen,
], (val, old) => {
  if ((val[0] === false && old[0] === true) || (val[1] === false && old[1] === true))
    setTimeout(() => currenItem.value = undefined, 200)
})

watch([page, itemsPerPage, sortBy, orderBy], (val, old) => {
  if (val[1] !== old[1])
    page.value = 1

  useCookie(`${table}TableOptions`).value = JSON.stringify({
    page: val[0],
    perPage: val[1],
    sortBy: val[2] === undefined ? currentTableOptions?.sortBy : val[2],
    orderBy: val[3] === undefined ? currentTableOptions?.orderBy : val[3],
  })
})
</script>

<template>
  <div>
    <VTabs
      v-model="activeTab"
      class="v-tabs-pill mb-6"
    >
      <VTab value="slot">
        <VIcon
          size="20"
          start
          icon="tabler-settings"
        />
        {{ t(`${table}-page.tab_system`) }}
      </VTab>
      <VTab value="custom">
        <VIcon
          size="20"
          start
          icon="tabler-pencil"
        />
        {{ t(`${table}-page.tab_custom`) }}
      </VTab>
    </VTabs>

    <VCard>
      <VCardText class="py-3">
        <span class="text-body-2 text-medium-emphasis">
          {{ activeTab === 'slot' ? t(`${table}-page.system_hint`) : t(`${table}-page.custom_hint`) }}
        </span>
      </VCardText>
      <VCardText class="d-flex flex-wrap py-4 pt-0 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>
        <VSpacer />
        <div
          v-if="activeTab === 'custom'"
          class="d-flex align-center flex-wrap gap-4"
        >
          <VBtn
            prepend-icon="tabler-plus"
            @click="isAddDialogOpen = !isAddDialogOpen"
          >
            {{ t(`${table}-page.add`) }}
          </VBtn>
        </div>
      </VCardText>
      <div class="category-table">
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :sort-by="sortTable"
          :loading="isLoading"
          :loading-text="t('loading')"
          :items="items"
          :items-length="total"
          class="text-no-wrap"
          @update:options="updateOptions"
        >
          <template #item.actions="{ item }">
            <div class="d-flex align-items-end justify-end">
              <IconBtn>
                <VIcon
                  icon="tabler-edit"
                  :title="t(`${table}-page.edit`)"
                  @click="showEditDialog(item)"
                />
              </IconBtn>
              <span v-if="!item.key">
                <IconBtn :disabled="usageCount(item) > 0">
                  <VIcon
                    icon="tabler-trash"
                    :title="t(`${table}-page.delete`)"
                    @click="deleteConfirmation(item)"
                  />
                </IconBtn>
                <VTooltip
                  v-if="usageCount(item) > 0"
                  activator="parent"
                  location="top"
                >
                  {{ t(`${table}-page.delete_blocked_hint`, { usage: usageText(item) }) }}
                </VTooltip>
              </span>
            </div>
          </template>
          <template #item.key="{ item }">
            <VChip
              color="primary"
              size="small"
              label
            >
              {{ item.key }}
            </VChip>
          </template>
          <template #item.name="{ item }">
            {{ item.name }}
          </template>
          <template #item.description="{ item }">
            <span class="text-wrap">
              {{ item.key ? slotDescription(item.key) : (item.description || '—') }}
            </span>
          </template>
          <template #item.model="{ item }">
            <template v-if="item.model">
              {{ item.model }}
            </template>
            <VChip
              v-else-if="item.key"
              color="secondary"
              size="small"
              label
            >
              {{ t(`${table}-page.model_from_code`) }}
              <VTooltip
                activator="parent"
                location="top"
              >
                {{ t(`${table}-page.model_from_code_hint`) }}
              </VTooltip>
            </VChip>
            <template v-else>
              —
            </template>
          </template>
          <template #item.used_by="{ item }">
            <span
              v-if="usageCount(item) > 0"
              class="text-wrap"
            >
              {{ usageText(item) }}
            </span>
            <span
              v-else
              class="text-medium-emphasis"
            >
              {{ t(`${table}-page.not_used`) }}
            </span>
          </template>
          <template #item.updated_at="{ item }">
            {{ item.updated_at ? formatDate(item.updated_at, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—' }}
          </template>
          <template #bottom>
            <div>
              <VDivider />

              <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 px-6 py-3">
                <AppSelect
                  v-model="itemsPerPage"
                  density="compact"
                  class="app-select flex-grow-0"
                  :items="[5, 10, 25, 50]"
                  style="max-inline-size: 5rem;min-inline-size: 5rem;"
                />

                <VPagination
                  v-model="page"
                  active-color="primary"
                  :length="Math.ceil(total / itemsPerPage)"
                  :total-visible="$vuetify.display.xs ? 1 : Math.min(Math.ceil(total / itemsPerPage), 5)"
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>

    <AIPromptAddEditDialog
      v-model:isDialogVisible="isAddDialogOpen"
      :ai-prompt="currenItem"
      @submit="updateList"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmDialogVisible"
      :confirmation-question="t('confirmation.sure')"
      cancel-msg=""
      :cancel-title="t('confirmation.cancel')"
      :confirm-msg="t('confirmation.deleted')"
      :confirm-title="t('confirmation.success')"
      @confirm="deleteItem"
    />
  </div>
</template>
